import Text from "components/atoms/Text";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "store";
import { useContext, useEffect, useState } from "react";
import { LogLevel, log } from "utils/LogUtil";
import { Button, COLOR_SYSTEM, Flex, Icon } from "components/atoms";
import { eventLog } from "utils/AmplitudeUtil";
import { User } from "common/models/types";
import { updateUser } from "api/userService";
import { ToastManager } from "components/organisms/CommonUI";
import { logout } from "store/slices/userSlice";
import { useNavigate } from "react-router-dom";
import Account from "components/templates/MyPageTemplate/Account";
import { changePassword } from "api/authService";
import ChangePassword from "components/templates/MyPageTemplate/Account/ChangePassword";
import { ConfirmManager } from "components/organisms/CommonUI/ConfirmPopup/ConfirmManager";

interface PasswordErrors {
  emptyCurrentPassword: boolean;
  emptyNewPassword: boolean;
  emptyConfirmPassword: boolean;
  shortPassword: boolean;
  notEqualPassword: boolean;
  invalidCurrentPassword: boolean;
}

const initErrors: PasswordErrors = {
  emptyCurrentPassword: false,
  emptyNewPassword: false,
  emptyConfirmPassword: false,
  shortPassword: false,
  notEqualPassword: false,
  invalidCurrentPassword: false,
};

const AccountPage = () => {
  const me = useSelector((state: RootState) => state.user.user);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  useEffect(() => {
    eventLog("계정관리_진입", {});
    log(LogLevel.VERBOSE, "AccountPage: mounted");
  }, []);

  const onChange = async (user: User) => {
    log(LogLevel.VERBOSE, "AccountPage: onChange", user);

    try {
      setIsLoading(true);
      await updateUser(user.id, user);
      setIsLoading(false);
    } catch (error) {
      log(LogLevel.VERBOSE, "AccountPage: onChange error", error);
      ToastManager.show({
        content: "계정 정보 수정에 실패했습니다.",
      });
    }

    ToastManager.show({
      content: (
        <Flex alignItem="center" gap="8px">
          <Icon name="Check Circle Filled" size={24} color={COLOR_SYSTEM.get("Blue")[300]} />
          <Text textType={"Body1SemiBold"} color={COLOR_SYSTEM.get("Gray")[900]}>
            계정 정보가 저장되었습니다.
          </Text>
        </Flex>
      ),
    });
  };

  const onLogout = async () => {
    eventLog("계정관리_로그아웃", {});
    await dispatch(logout()).unwrap();
    navigate("/login");
  };

  const [errors, setErrors] = useState<PasswordErrors>(initErrors);

  const handleSubmit = async (currentPassword: string, newPassword: string) => {
    setIsLoading(true);
    try {
      await changePassword(currentPassword, newPassword);
      ConfirmManager.hide();
      eventLog("비밀번호변경_성공");
      ToastManager.show({
        content: (
          <Flex alignItem="center" gap="8px">
            <Icon name="Check Circle Filled" size={24} color={COLOR_SYSTEM.get("Blue")[300]} />
            <Text textType={"Body1SemiBold"} color={COLOR_SYSTEM.get("Gray")[900]}>
              비밀번호가 성공적으로 변경되었습니다.
            </Text>
          </Flex>
        ),
      });
    } catch (error: any) {
      if (error.message === "현재 비밀번호가 일치하지 않습니다.") {
        setErrors((prev) => ({ ...prev, invalidCurrentPassword: true }));
      }
      eventLog("비밀번호변경_실패", { error: error.message });
      ToastManager.show({
        content: (
          <Flex alignItem="center" gap="8px">
            <Icon name="X" size={24} color={COLOR_SYSTEM.get("Red")[300]} />
            <Text textType={"Body1SemiBold"} color={COLOR_SYSTEM.get("Gray")[900]}>
              {"비밀번호 변경 중 오류가 발생했습니다."}
            </Text>
          </Flex>
        ),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setErrors((prev) => {
      const newErrors = { ...prev };
      switch (field) {
        case "currentPassword":
          newErrors.invalidCurrentPassword = false;
          break;
      }
      return newErrors;
    });
  };

  const handleOpenChangePassword = () => {
    ConfirmManager.show({
      body: (
        <ChangePassword
          onClose={() => ConfirmManager.hide()}
          onSubmit={handleSubmit}
          isLoading={isLoading}
          errors={errors}
          onInputChange={handleInputChange}
        />
      ),
      showBackdrop: true,
      backdropClose: false,
      showCloseButton: true,
    });
  };

  return (
    <Account
      me={me}
      onChange={onChange}
      isLoading={isLoading}
      onLogout={onLogout}
      onChangePassword={handleOpenChangePassword}
    />
  );
};

export default AccountPage;
